import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import {
  useCreateLeave,
  useDecideLeave,
  useLeave,
  useLeaveBalances,
  useMe,
  usePeople,
} from '../hooks/useOps';
import type { LeaveBalanceView, LeaveRequest, LeaveType } from '../api/types';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';

const LEAVE_TYPES: LeaveType[] = ['annual', 'sick', 'unpaid', 'other'];
const EMPTY = { personId: '', leaveType: 'annual' as LeaveType, startDate: '', endDate: '' };

export function LeavePage() {
  const { data: me } = useMe();
  const canDecide = me?.capability.canDecideLeave ?? false;
  const year = new Date().getFullYear();

  const { data: requests = [], isLoading, error } = useLeave();
  const { data: balances = [] } = useLeaveBalances(year);
  const { data: people = [] } = usePeople({ status: 'active' });
  const create = useCreateLeave();
  const decide = useDecideLeave();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  const submit = async () => {
    setFormError(null);
    try {
      await create.mutateAsync({
        personId: form.personId,
        leaveType: form.leaveType,
        startDate: form.startDate,
        endDate: form.endDate,
      });
      setOpen(false);
      setForm(EMPTY);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const onDecide = async (req: LeaveRequest, decision: 'approved' | 'rejected') => {
    try {
      await decide.mutateAsync({ id: req.id, decision });
    } catch (e) {
      // Over-balance block: offer an explicit override (plan edge case).
      const msg = (e as Error).message;
      if (decision === 'approved' && /remaining annual leave/i.test(msg)) {
        if (window.confirm(`${msg}\n\nApprove anyway (over balance)?`)) {
          await decide.mutateAsync({ id: req.id, decision, allowOverbalance: true });
        }
      } else {
        window.alert(msg);
      }
    }
  };

  const columns: GridColDef<LeaveRequest>[] = [
    { field: 'personId', headerName: 'Person', minWidth: 150, flex: 1, valueGetter: (v) => nameOf(v as string) },
    {
      field: 'leaveType',
      headerName: 'Type',
      width: 100,
      renderCell: (p) => <Chip size="small" variant="outlined" label={p.value} />,
    },
    { field: 'startDate', headerName: 'From', width: 120, valueGetter: (v) => formatDate(v as string) },
    { field: 'endDate', headerName: 'To', width: 120, valueGetter: (v) => formatDate(v as string) },
    { field: 'days', headerName: 'Days', width: 80 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => (
        <Chip size="small" variant="outlined" color={statusColor(p.value)} label={humanizeStatus(p.value)} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      renderCell: (p) =>
        p.row.status === 'pending' && canDecide ? (
          <Stack direction="row" gap={0.5}>
            <Button size="small" color="success" onClick={() => onDecide(p.row, 'approved')}>
              Approve
            </Button>
            <Button size="small" color="error" onClick={() => onDecide(p.row, 'rejected')}>
              Reject
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Leave"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Request leave
          </Button>
        }
      />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Annual-leave balances · {year}
      </Typography>
      <Paper variant="outlined" sx={{ height: 280, mb: 3 }}>
        <DataGrid<LeaveBalanceView>
          rows={balances}
          getRowId={(r) => r.personId}
          columns={[
            { field: 'personName', headerName: 'Person', minWidth: 160, flex: 1 },
            { field: 'entitled', headerName: 'Entitled', width: 110 },
            { field: 'taken', headerName: 'Taken', width: 100 },
            { field: 'pending', headerName: 'Pending', width: 100 },
            {
              field: 'remaining',
              headerName: 'Remaining',
              width: 120,
              renderCell: (p) => (
                <Chip size="small" variant="outlined" color={p.value <= 0 ? 'error' : 'success'} label={p.value} />
              ),
            },
          ]}
          disableRowSelectionOnClick
          hideFooter
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Requests
      </Typography>
      <Paper variant="outlined" sx={{ height: 460 }}>
        <DataGrid
          rows={requests}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request leave</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Person"
              value={form.personId}
              onChange={(e) => setForm({ ...form, personId: e.target.value })}
              fullWidth
            >
              {people.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Type"
              value={form.leaveType}
              onChange={(e) => setForm({ ...form, leaveType: e.target.value as LeaveType })}
              fullWidth
            >
              {LEAVE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" gap={2}>
              <TextField label="From" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="To" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={create.isPending || !form.personId || !form.startDate || !form.endDate}
            onClick={submit}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
